import {
  createTableMdComponents,
  streamdownRehypePlugins,
  type MarkdownComponents,
} from '@heidi/answer-kit';
import { cn } from '@heidi/utils';
import { AlertTriangle, Ban, CheckCircle2 } from 'lucide-react';
import { useIntl } from 'react-intl';
import { Streamdown } from 'streamdown';

import type { ComputerUseStatus } from '../../../contracts/projects/computer-use-status';
import { computerUseMessages } from '../../../data/projects/components/ComputerUseTaskBlock.status.utils';

// Re-exported rather than declared: `ComputerUseTaskBlock.status.utils.ts` reads the
// same union from `/data`, which may not reach this zone, so the declaration sits in
// `/contracts`. This path publishes exactly the names it always published.
export type { ComputerUseStatus };

const TERMINAL_REASON_MARKDOWN_CLASS = cn(
  'min-w-0 flex-1 break-words text-xs leading-snug [overflow-wrap:anywhere]',
  '[&_h1]:text-xs [&_h2]:text-xs [&_h3]:text-xs [&_h4]:text-xs',
  '[&_h1]:my-0 [&_h2]:my-0 [&_h3]:my-0 [&_h4]:my-0',
  '[&_h1]:!font-body [&_h2]:!font-body [&_h3]:!font-body',
  '[&_code]:text-xs [&_hr]:my-2 [&_ol]:ps-4 [&_ul]:ps-4'
);

const TERMINAL_REASON_MD_COMPONENTS = createTableMdComponents({
  wrapperClassName:
    'my-2 grid grid-cols-1 overflow-hidden rounded-md border border-border/70 first:!mt-0 last:!mb-0',
  headerNoWrap: true,
  cellTextClass: 'text-xs',
}) as MarkdownComponents;

const AGENT_FINAL_RESPONSE_LABEL = /^\s*Agent final response:\s*/i;
const OUTCOME_JUDGMENT_LABEL = /(^|\s)Outcome judgment:\s*/gi;

const prepareTerminalReasonMarkdown = (reason: string): string =>
  reason
    .replace(AGENT_FINAL_RESPONSE_LABEL, '')
    .replace(OUTCOME_JUDGMENT_LABEL, '\n\n')
    .trim();

export const TerminalReason = ({
  status,
  reason,
}: {
  status: ComputerUseStatus;
  reason: string;
}) => {
  const isError = status === 'error';
  return (
    <div
      className={cn(
        'flex items-start gap-2 border-t pt-3',
        isError ? 'border-validation-destructive-border/50' : 'border-border/60'
      )}
    >
      {isError ? (
        <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-validation-destructive" />
      ) : (
        <Ban className="mt-0.5 size-3.5 shrink-0 text-text-tertiary" />
      )}
      <div
        className={cn(
          TERMINAL_REASON_MARKDOWN_CLASS,
          isError ? 'text-validation-destructive' : 'text-text-secondary'
        )}
      >
        <Streamdown
          mode="static"
          controls={false}
          className="space-y-2"
          components={TERMINAL_REASON_MD_COMPONENTS}
          rehypePlugins={streamdownRehypePlugins}
        >
          {prepareTerminalReasonMarkdown(reason)}
        </Streamdown>
      </div>
    </div>
  );
};

export const StatusBadge = ({
  status,
  stepCount,
  awaitingApproval = false,
}: {
  status: ComputerUseStatus;
  stepCount?: number;
  awaitingApproval?: boolean;
}) => {
  const intl = useIntl();
  if (status === 'running' && awaitingApproval) {
    return (
      <span
        className="inline-flex items-center gap-1.5 rounded-full bg-background-tertiary px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-text-secondary"
        data-testid="cu-awaiting-approval-badge"
      >
        <span className="relative flex size-1.5">
          <span className="absolute inline-flex size-full animate-ping rounded-full bg-text-tertiary opacity-60" />
          <span className="relative inline-flex size-1.5 rounded-full bg-text-tertiary" />
        </span>
        {intl.formatMessage({
          defaultMessage: 'Waiting for approval',
          id: 'kjUNOWZ7G5',
        })}
      </span>
    );
  }
  if (status === 'running') {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-background-tertiary px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-text-secondary">
        <span className="relative flex size-1.5">
          <span className="absolute inline-flex size-full animate-ping rounded-full bg-validation-info opacity-60" />
          <span className="relative inline-flex size-1.5 rounded-full bg-validation-info" />
        </span>
        {typeof stepCount === 'number' && stepCount > 0
          ? intl.formatMessage(computerUseMessages.runningWithStep, { count: stepCount })
          : intl.formatMessage(computerUseMessages.running)}
      </span>
    );
  }
  if (status === 'success') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-validation-success-muted px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-validation-success">
        <CheckCircle2 className="size-3" />
        {intl.formatMessage(computerUseMessages.done)}
      </span>
    );
  }
  if (status === 'cancelled') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-background-tertiary px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-text-secondary">
        <Ban className="size-3" />
        {intl.formatMessage(computerUseMessages.cancelled)}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-validation-destructive-muted px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-validation-destructive">
      <AlertTriangle className="size-3" />
      {intl.formatMessage(computerUseMessages.stopped)}
    </span>
  );
};
